import React, { useState, useEffect, createContext, useContext, useRef, useCallback, useMemo } from 'react';
import './App.css';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot, addDoc, collection } from 'firebase/firestore';

// --- Firebase Services Import ---
import { auth, db, storage } from './firebase';

// --- Component Imports ---
// YOU MUST CREATE THESE FILES FOR THE APP TO WORK
import { HomePage } from './components/HomePage';
import { AboutOrganizationPage } from './components/AboutOrganizationPage';
import { CommitteesPage } from './components/CommitteesPage';
import { AboutConferencePage } from './components/AboutConferencePage';
import { MeetTheTeamPage } from './components/MeetTheTeamPage';
import { InfoPackPage } from './components/InfoPackPage';
import { RulesPreparationPage } from './components/RulesPreparationPage';
import { ContactPage } from './components/ContactPage';
import { PricingPage, PackageDetailPage } from './PricingComponents';

// These are the files we will create in the next steps:
import AdminDashboardPage from './pages/AdminDashboardPage'; 
import {RegisterNowPage} from './pages/RegisterNowPage'; 
import LoginPage from './pages/LoginPage';
import DelegatePortalPage from './pages/DelegatePortalPage';
import TeacherDashboardPage from './pages/TeacherDashboardPage';

// --- Context ---
const FirebaseContext = createContext();
export const useFirebase = () => useContext(FirebaseContext);

// --- Error Boundary ---
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error: error };
    }
    componentDidCatch(error, errorInfo) {
        console.error("Uncaught error:", error, errorInfo);
        if (this.props.logError) this.props.logError(error, errorInfo.componentStack);
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="flex flex-col items-center justify-center h-screen bg-red-100 p-8 text-red-800">
                    <h1 className="text-2xl font-bold mb-4">Something went wrong.</h1>
                    <button onClick={() => window.location.reload()} className="bg-red-600 text-white px-4 py-2 rounded">Reload Page</button>
                </div>
            );
        }
        return this.props.children;
    }
}

// --- Firebase Provider (THE FIX CORE) ---
const FirebaseProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isUserRoleReady, setIsUserRoleReady] = useState(false);
    const [appSettings, setAppSettings] = useState({});
    const [userThemePreference, setUserThemePreference] = useState(false);
    const isMounted = useRef(true);

    // Theme Logic
    useEffect(() => {
        isMounted.current = true;
        const localTheme = localStorage.getItem('user-theme-preference');
        if (localTheme === 'dark') {
            setUserThemePreference(true);
            document.body.classList.add('dark-theme');
        }
        return () => { isMounted.current = false; };
    }, []);

    const saveUserThemePreference = useCallback((isDark) => {
        setUserThemePreference(isDark);
        localStorage.setItem('user-theme-preference', isDark ? 'dark' : 'light');
        if (isDark) document.body.classList.add('dark-theme');
        else document.body.classList.remove('dark-theme');
    }, []);

    // Error Logging
    const logError = useCallback(async (error, stack) => {
        console.error(error);
        if (!auth.currentUser) return;
        try {
            await addDoc(collection(db, `errorLogs`), {
                message: error.message || 'Unknown',
                stack: stack || '',
                timestamp: new Date(),
                userId: auth.currentUser.uid,
                path: window.location.pathname,
            });
        } catch (e) { console.error("Failed to log to Firestore", e); }
    }, []);

    // --- CRITICAL FIX: Robust User Fetching ---
    const fetchUserData = useCallback(async (firebaseUser, retryCount = 0) => {
        if (!isMounted.current || !firebaseUser) return;

        try {
            const userDocRef = doc(db, 'users', firebaseUser.uid);
            const userDocSnap = await getDoc(userDocRef);

            if (userDocSnap.exists()) {
                const userData = userDocSnap.data();
                setUser(prev => ({ ...prev, ...userData, role: userData.userType }));
            } else {
                // RACE CONDITION FIX:
                // If the user was created < 10 seconds ago, and doc is missing, it's a latency issue.
                const creationTime = new Date(firebaseUser.metadata.creationTime).getTime();
                const now = new Date().getTime();
                
                if (now - creationTime < 10000 && retryCount < 3) {
                    console.log(`User doc not found yet. Retrying... (${retryCount + 1}/3)`);
                    setTimeout(() => fetchUserData(firebaseUser, retryCount + 1), 1000);
                    return;
                }
                
                // Genuine unregistered user
                setUser(prev => ({ ...prev, role: 'unregistered' }));
            }
        } catch (error) {
            console.error("Error fetching user data:", error);
        } finally {
            if (isMounted.current) setIsUserRoleReady(true);
        }
    }, []);

    const checkAdminStatus = useCallback(async (uid) => {
        try {
            const adminDoc = await getDoc(doc(db, 'admins', uid));
            if (isMounted.current) setIsAdmin(adminDoc.exists());
        } catch (e) { console.error(e); }
    }, []);

    // Auth Listener
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            if (!isMounted.current) return;
            
            if (currentUser) {
                setUser({ uid: currentUser.uid, email: currentUser.email }); // Set basic info immediately
                fetchUserData(currentUser);
                checkAdminStatus(currentUser.uid);
                setIsAuthReady(true);
            } else {
                setUser(null);
                setIsAdmin(false);
                setIsUserRoleReady(false); // Reset role ready on logout
                setIsAuthReady(true);
            }
        });
        return () => unsubscribe();
    }, [fetchUserData, checkAdminStatus]);

    // Settings Listener
    useEffect(() => {
        const unsub = onSnapshot(doc(db, 'appSettings', 'general'), (doc) => {
            if (doc.exists()) setAppSettings(doc.data());
        });
        return () => unsub();
    }, []);

    const value = useMemo(() => ({
        user, setUser, isAuthReady, isUserRoleReady, isAdmin,
        userId: user?.uid,
        db, auth, storage,
        ...appSettings,
        logError, saveUserThemePreference, userThemePreference
    }), [user, isAuthReady, isUserRoleReady, isAdmin, appSettings, userThemePreference, logError, saveUserThemePreference]);

    return (
        <FirebaseContext.Provider value={value}>
            <ErrorBoundary logError={logError}>
                {children}
            </ErrorBoundary>
        </FirebaseContext.Provider>
    );
};

// --- Navigation & Layout ---
const Navbar = ({ navigate, isMobileMenuOpen, toggleMobileMenu }) => {
    const { user, isAuthReady, isUserRoleReady, isAdmin, auth, saveUserThemePreference } = useFirebase();
    const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);

    const handleSignOut = async () => {
        await signOut(auth);
        saveUserThemePreference(false);
        navigate('home');
    };

    return (
        <nav className="p-4 shadow-md sticky top-0 z-40" style={{ backgroundColor: 'var(--color-nav-bg)', color: 'var(--color-nav-text)' }}>
            <div className="container mx-auto flex justify-between items-center">
                <button onClick={() => navigate('home')} className="text-2xl font-bold">EthosMUN</button>
                
                {/* Desktop Menu */}
                <div className="hidden md:flex space-x-6 items-center">
                    <div className="relative">
                        <button onClick={() => setIsAboutDropdownOpen(!isAboutDropdownOpen)} className="flex items-center hover:text-gray-300">
                            About Us <span className="ml-1">▼</span>
                        </button>
                        {isAboutDropdownOpen && (
                            <div className="absolute left-0 top-full mt-2 w-48 bg-blue-800 rounded shadow-xl z-50">
                                <button onClick={() => navigate('about-organization')} className="block w-full text-left px-4 py-2 hover:bg-blue-700">Organization</button>
                                <button onClick={() => navigate('about-conference')} className="block w-full text-left px-4 py-2 hover:bg-blue-700">Conference</button>
                                <button onClick={() => navigate('meet-the-team')} className="block w-full text-left px-4 py-2 hover:bg-blue-700">Team</button>
                            </div>
                        )}
                    </div>
                    <button onClick={() => navigate('committees')} className="hover:text-gray-300">Committees</button>
                    <button onClick={() => navigate('pricing')} className="hover:text-gray-300">Pricing</button>
                    <button onClick={() => navigate('contact')} className="hover:text-gray-300">Contact</button>

                    {/* Auth Buttons */}
                    {isAuthReady && (
                        <>
                            {!user ? (
                                <button onClick={() => navigate('login')} className="bg-blue-600 px-4 py-2 rounded-full hover:bg-blue-500 font-bold text-white shadow">Log In</button>
                            ) : (
                                <>
                                    {isUserRoleReady && (
                                        user.role === 'teacher' 
                                        ? <button onClick={() => navigate('teacher-dashboard')} className="hover:text-blue-300">Dashboard</button>
                                        : <button onClick={() => navigate('portal')} className="hover:text-blue-300">Portal</button>
                                    )}
                                    {isAdmin && (
                                        <button onClick={() => navigate('admin')} className="bg-yellow-400 text-black px-4 py-2 rounded-full font-bold hover:bg-yellow-300">Admin</button>
                                    )}
                                    <button onClick={handleSignOut} className="bg-red-600 px-4 py-2 rounded-full font-bold hover:bg-red-500 text-white shadow">Sign Out</button>
                                </>
                            )}
                        </>
                    )}
                </div>

                {/* Mobile Toggle */}
                <button className="md:hidden" onClick={toggleMobileMenu}>☰</button>
            </div>
            
            {/* Mobile Menu */}
            {isMobileMenuOpen && (
                <div className="md:hidden mt-4 flex flex-col space-y-2 pb-4">
                    <button onClick={() => navigate('home')}>Home</button>
                    <button onClick={() => navigate('committees')}>Committees</button>
                    {!user ? (
                        <button onClick={() => navigate('login')} className="bg-blue-600 py-2 rounded text-white">Log In</button>
                    ) : (
                        <button onClick={handleSignOut} className="bg-red-600 py-2 rounded text-white">Sign Out</button>
                    )}
                </div>
            )}
        </nav>
    );
};

const PageRouter = ({ currentPage, navigate }) => {
    const { userThemePreference } = useFirebase();
    
    switch (currentPage) {
        case 'home': return <HomePage navigate={navigate} isDarkTheme={userThemePreference} />;
        case 'about-organization': return <AboutOrganizationPage />;
        case 'about-conference': return <AboutConferencePage />;
        case 'committees': return <CommitteesPage />;
        case 'meet-the-team': return <MeetTheTeamPage />;
        case 'info-pack': return <InfoPackPage />;
        case 'contact': return <ContactPage />;
        case 'rules-preparation': return <RulesPreparationPage />;
        case 'pricing': return <PricingPage navigate={navigate} />;
        
        // These will be external files soon
        case 'register': return <RegisterNowPage navigate={navigate} />;
        case 'login': return <LoginPage navigate={navigate} />;
        case 'portal': return <DelegatePortalPage navigate={navigate} />;
        case 'teacher-dashboard': return <TeacherDashboardPage navigate={navigate} />;
        case 'admin': return <AdminDashboardPage />;
        
        // Package Details
        case 'package-details-basic': return <PackageDetailPage packageId="basic" navigate={navigate} />;
        case 'package-details-standard': return <PackageDetailPage packageId="standard" navigate={navigate} />;
        case 'package-details-premium': return <PackageDetailPage packageId="premium" navigate={navigate} />;
        case 'package-details-vip': return <PackageDetailPage packageId="vip" navigate={navigate} />;
        
        default: return <HomePage navigate={navigate} isDarkTheme={userThemePreference} />;
    }
};

const MainLayout = () => {
    const [currentPage, setCurrentPage] = useState('home');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const navigate = (page) => {
        setCurrentPage(page);
        setIsMobileMenuOpen(false);
        window.scrollTo(0, 0);
    };

    return (
        <div className="min-h-screen font-sans" style={{ backgroundColor: 'var(--color-body-bg)', color: 'var(--color-body-text)' }}>
            <Navbar 
                navigate={navigate} 
                isMobileMenuOpen={isMobileMenuOpen} 
                toggleMobileMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
            />
            <main>
                <PageRouter currentPage={currentPage} navigate={navigate} />
            </main>
            <footer className="py-8 mt-16 text-center" style={{ backgroundColor: 'var(--color-footer-bg)', color: 'var(--color-footer-text)' }}>
                <p>&copy; {new Date().getFullYear()} EthosMUN. All rights reserved.</p>
            </footer>
        </div>
    );
};

const App = () => {
    return (
        <FirebaseProvider>
            <MainLayout />
        </FirebaseProvider>
    );
};

export default App;